import React, { useState, useCallback } from 'react';
import { PermissionOverride } from '../../types/permissions';
import { getUserPermissions, updateUserPermissions, clearUserPermissions } from '../../api/permissions';
import { ROLE_LABELS } from '../../utils/roles';
import { PermissionMatrix } from './PermissionMatrix';
import { User } from '../../types';
import { apiList } from '../../api/apiClient';

export const UserOverridesTab: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [overrides, setOverrides] = useState<PermissionOverride[]>([]);
  const [originalOverrides, setOriginalOverrides] = useState<PermissionOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchUsers = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    setError(null);
    try {
      const allUsers = await apiList('users');
      const q = query.toLowerCase();
      const filtered = (allUsers || []).filter((u: any) =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.first_name || '').toLowerCase().includes(q) ||
        (u.last_name || '').toLowerCase().includes(q)
      );
      setSearchResults(filtered);
    } catch (err) {
      console.error('[RBAC] User search failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to search users.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const selectUser = useCallback(async (user: User) => {
    setSelectedUser(user);
    setSearchResults([]);
    setSearchQuery('');
    setLoading(true);
    setSaved(false);
    setError(null);
    try {
      const data = await getUserPermissions(user.id);
      setOverrides(data);
      setOriginalOverrides(JSON.parse(JSON.stringify(data)));
    } catch (err) {
      console.error('[RBAC] Failed to load user permissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user permissions.');
      setOverrides([]);
      setOriginalOverrides([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const hasChanges = JSON.stringify(overrides) !== JSON.stringify(originalOverrides);

  const handleChange = (page: string, operation: string, value: boolean | null) => {
    setOverrides(prev => {
      const existing = prev.find(o => o.page === page);
      const key = `can_${operation}` as keyof PermissionOverride;

      if (existing) {
        return prev.map(o =>
          o.page === page ? { ...o, [key]: value } : o
        );
      } else {
        const newOverride: PermissionOverride = {
          page,
          can_view: null,
          can_create: null,
          can_update: null,
          can_delete: null,
          can_export: null,
          [key]: value,
        };
        return [...prev, newOverride];
      }
    });
  };

  const handleReset = (page: string) => {
    setOverrides(prev => prev.filter(o => o.page !== page));
  };

  const handleClearAll = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await clearUserPermissions(selectedUser.id);
      const authoritative = await getUserPermissions(selectedUser.id);
      setOverrides(authoritative);
      setOriginalOverrides(JSON.parse(JSON.stringify(authoritative)));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('[RBAC] Failed to clear user permissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear user permissions.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const meaningful = overrides.filter(o =>
        o.can_view !== null || o.can_create !== null || o.can_update !== null ||
        o.can_delete !== null || o.can_export !== null
      );
      await updateUserPermissions(selectedUser.id, meaningful);
      const authoritative = await getUserPermissions(selectedUser.id);
      setOriginalOverrides(JSON.parse(JSON.stringify(authoritative)));
      setOverrides(authoritative);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('[RBAC] Failed to save user permissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to save user permissions.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="relative mb-6">
        <input
          type="text"
          value={searchQuery}
          onChange={e => {
            setSearchQuery(e.target.value);
            searchUsers(e.target.value);
          }}
          placeholder="Search users by name or email..."
          className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
        {searching && (
          <div className="absolute right-3 top-3 text-gray-400 text-xs">Searching...</div>
        )}

        {searchResults.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {searchResults.map(user => (
              <button
                key={user.id}
                onClick={() => selectUser(user)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0"
              >
                <div className="text-sm font-medium text-gray-800">{user.name || user.email}</div>
                <div className="text-xs text-gray-500">{user.email} — {ROLE_LABELS[user.role]}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div role="alert" className="mb-4 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {selectedUser ? (
        <div>
          <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-semibold text-gray-800">{selectedUser.name || selectedUser.email}</h3>
              <p className="text-sm text-gray-500">
                {selectedUser.email} — Role: <span className="font-medium">{ROLE_LABELS[selectedUser.role]}</span>
              </p>
            </div>
            <div className="flex items-center gap-3">
              {overrides.length > 0 && (
                <button
                  onClick={handleClearAll}
                  disabled={saving}
                  className="text-sm text-red-500 hover:text-red-700 font-medium transition-colors"
                >
                  Clear All Overrides
                </button>
              )}
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setOverrides([]);
                  setOriginalOverrides([]);
                }}
                disabled={saving}
                className="text-sm text-gray-400 hover:text-gray-600"
              >
                Change User
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading permissions...</div>
          ) : (
            <PermissionMatrix
              overrides={overrides}
              onChange={handleChange}
              showReset={true}
              onReset={handleReset}
              userOverridePages={overrides.filter(o =>
                o.can_view !== null || o.can_create !== null || o.can_update !== null ||
                o.can_delete !== null || o.can_export !== null
              ).map(o => o.page)}
            />
          )}

          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            {saved ? (
              <span className="text-sm text-green-600 font-medium">Permissions saved successfully!</span>
            ) : <div />}
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                hasChanges && !saving
                  ? 'bg-brand-600 text-white hover:bg-brand-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center py-16 text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <p>Search for a user above to manage their permission overrides</p>
        </div>
      )}
    </div>
  );
};
